import React, { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency } from '@/lib/utils'
import { jobMarginFull, formatMarginPct } from '@/lib/margin'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, AreaChart, Area, CartesianGrid,
} from 'recharts'
import {
  TrendingUp, MessageSquare, Truck, Star, DollarSign,
  CheckCircle2, Clock, AlertTriangle, ArrowUpRight,
  ArrowDownRight, Target, AlertCircle, Pause,
} from 'lucide-react'
import {
  subDays, startOfDay, endOfDay, format, eachDayOfInterval,
  eachWeekOfInterval, isWithinInterval, parseISO,
} from 'date-fns'
import { HOLD_STATUSES } from '@/pages/field/jobStatusTransition'
import RevenueTicker from '@/pages/analytics/RevenueTicker'
import RevenueProjections from '@/pages/analytics/RevenueProjections'
import { useIsMobile } from '@/lib/useIsMobile'

type Range = '7d' | '30d' | '90d' | 'custom'

const RANGE_OPTIONS: { id: Range; label: string }[] = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
]

const COLORS = {
  primary: 'var(--accent)',
  neon: 'var(--positive)',
  coral: 'var(--danger)',
  amber: 'var(--warning)',
  blue: 'var(--info)',
  purple2: 'var(--accent-text)',
}

function getDateRange(range: Range, customStart?: Date, customEnd?: Date) {
  const end = endOfDay(new Date())
  let start: Date
  switch (range) {
    case '7d': start = startOfDay(subDays(new Date(), 6)); break
    case '30d': start = startOfDay(subDays(new Date(), 29)); break
    case '90d': start = startOfDay(subDays(new Date(), 89)); break
    case 'custom': start = customStart ? startOfDay(customStart) : startOfDay(subDays(new Date(), 29)); break
    default: start = startOfDay(subDays(new Date(), 29))
  }
  return { start, end }
}

function inRange(dateStr: string, start: Date, end: Date) {
  try { return isWithinInterval(parseISO(dateStr), { start, end }) } catch { return false }
}

function buildTimeSeries(items: any[], dateField: string, start: Date, end: Date, range: Range, valueField?: string) {
  const useWeeks = range === '90d'
  const groups = useWeeks ? eachWeekOfInterval({ start, end }) : eachDayOfInterval({ start, end })
  return groups.map(groupStart => {
    const groupEnd = useWeeks
      ? endOfDay(subDays(new Date(groupStart.getTime() + 7 * 24 * 60 * 60 * 1000), 1))
      : endOfDay(groupStart)
    const filtered = items.filter(item => {
      const d = item[dateField] ? parseISO(item[dateField]) : null
      return d && isWithinInterval(d, { start: groupStart, end: groupEnd })
    })
    return {
      date: format(groupStart, useWeeks ? 'MMM d' : range === '7d' ? 'EEE' : 'MMM d'),
      count: filtered.length,
      value: valueField ? filtered.reduce((s: number, i: any) => s + (parseFloat(i[valueField]) || 0), 0) : filtered.length,
    }
  })
}

function StatCard({ label, value, sub, color, icon: Icon, trend }: any) {
  return (
    <motion.div whileHover={{ y: -2 }}
      style={{ background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -24, right: -24, width: 80, height: 80, background: `radial-gradient(circle, color-mix(in srgb, ${color} 13%, transparent) 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: `color-mix(in srgb, ${color} 9%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 15%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon style={{ width: 16, height: 16, color }} />
        </div>
        {trend !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {trend >= 0
              ? <ArrowUpRight style={{ width: 13, height: 13, color: 'var(--positive)' }} />
              : <ArrowDownRight style={{ width: 13, height: 13, color: 'var(--danger)' }} />}
            <span style={{ fontSize: 12, fontWeight: 700, color: trend >= 0 ? 'var(--positive)' : 'var(--danger)' }}>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <p style={{ fontSize: 26, fontWeight: 900, color: 'var(--ink-primary)', margin: '0 0 4px', letterSpacing: '-0.03em' }}>{value}</p>
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', margin: '0 0 2px' }}>{label}</p>
      {sub && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{sub}</p>}
    </motion.div>
  )
}

function Section({ title, children }: any) {
  return (
    <div style={{ background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 16, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{title}</h3>
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  )
}

function CustomTooltip({ active, payload, label, isCurrency }: any) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'var(--ink-primary)' }}>
      <p style={{ fontSize: 11, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ fontSize: 13, fontWeight: 700, color: p.color || 'var(--ink-primary)', margin: 0 }}>
          {isCurrency ? formatCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  )
}

function BarRow({ label, value, total, color }: any) {
  const pct = total > 0 ? (value / total) * 100 : 0
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 12, color: 'var(--ink-secondary)', width: 90, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 5, background: 'var(--field-bg)', borderRadius: 100, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 100 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, width: 24, textAlign: 'right', flexShrink: 0 }}>{value}</span>
    </div>
  )
}

export default function Analytics() {
  const { workspace } = useAuth()
  const isMobile = useIsMobile()
  const [range, setRange] = useState<Range>('30d')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [activeSection, setActiveSection] = useState('all')

  const { start, end } = useMemo(() => {
    if (range === 'custom' && customStart && customEnd) {
      return getDateRange('custom', new Date(customStart), new Date(customEnd))
    }
    return getDateRange(range)
  }, [range, customStart, customEnd])

  // ── Lean queries — only fetch columns we actually use ──────────────────────

  const { data: conversations = [], isError: convError } = useQuery({
    queryKey: ['analytics-conversations', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('conversations')
        .select('id, status, priority, channel, created_at')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: invoices = [], isError: invError } = useQuery({
    queryKey: ['analytics-invoices', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('invoices')
        .select('id, status, total, amount_due, amount_paid, paid_at, created_at')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: quotes = [], isError: quoteError } = useQuery({
    queryKey: ['analytics-quotes', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('quotes')
        .select('id, status, total, created_at')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: deals = [], isError: dealError } = useQuery({
    queryKey: ['analytics-deals', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('deals')
        .select('id, status, value, stage_name, created_at')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: fieldJobs = [], isError: jobError } = useQuery({
    queryKey: ['analytics-field-jobs', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('field_jobs')
        .select('id, status, assigned_agent_name, payment_collected, payment_amount, created_at, actual_end, scheduled_date, total')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: reviews = [], isError: reviewError } = useQuery({
    queryKey: ['analytics-reviews', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('review_records')
        .select('id, rating, platform, status, created_at')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: nps = [], isError: npsError } = useQuery({
    queryKey: ['analytics-nps', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('nps_surveys')
        .select('id, score, created_at')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  // Staff-only: line costs for margin. field_job_line_items is workspace-isolation
  // RLS (no portal/vendor policy), so unit_cost is staff-only — Analytics itself
  // is a /app (staff) route. Cost/margin never reaches a customer surface.
  const { data: jobLines = [] } = useQuery({
    queryKey: ['analytics-job-lines', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('field_job_line_items')
        .select('field_job_id, qty, unit_price, unit_cost')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  // Staff-only: vendor bills (job-level cost addend) + which jobs are subbed.
  // Both tables are workspace-isolation only (no vendor/portal policy).
  const { data: jobBills = [] } = useQuery({
    queryKey: ['analytics-vendor-bills', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('vendor_bills')
        .select('field_job_id, amount')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: jobAssignments = [] } = useQuery({
    queryKey: ['analytics-vendor-assignments', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('vendor_assignments')
        .select('field_job_id')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  // Staff-only: in-house labor cost (third job-level addend). field_job_labor is
  // workspace-isolation only (no vendor/portal policy).
  const { data: jobLabor = [] } = useQuery({
    queryKey: ['analytics-field-job-labor', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('field_job_labor')
        .select('field_job_id, amount')
        .eq('workspace_id', workspace.id)
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const anyError = convError || invError || quoteError || dealError || jobError || reviewError || npsError

  // ── Filter to range ────────────────────────────────────────────────────────
  const rc = useMemo(() => conversations.filter((c: any) => inRange(c.created_at, start, end)), [conversations, start, end])
  const ri = useMemo(() => invoices.filter((i: any) => inRange(i.created_at, start, end)), [invoices, start, end])
  const rq = useMemo(() => quotes.filter((q: any) => inRange(q.created_at, start, end)), [quotes, start, end])
  const rd = useMemo(() => deals.filter((d: any) => inRange(d.created_at, start, end)), [deals, start, end])
  const rj = useMemo(() => fieldJobs.filter((j: any) => inRange(j.created_at, start, end)), [fieldJobs, start, end])
  const rr = useMemo(() => reviews.filter((r: any) => inRange(r.created_at, start, end)), [reviews, start, end])
  const rn = useMemo(() => nps.filter((n: any) => inRange(n.created_at, start, end)), [nps, start, end])

  // ── Metrics ────────────────────────────────────────────────────────────────
  const revenue = useMemo(() => {
    const collected = ri.filter((i: any) => i.status === 'paid').reduce((s: number, i: any) => s + (i.total || 0), 0)
    const outstanding = ri.filter((i: any) => ['sent', 'viewed', 'partial'].includes(i.status)).reduce((s: number, i: any) => s + (i.amount_due || 0), 0)
    const overdue = ri.filter((i: any) => i.status === 'overdue').reduce((s: number, i: any) => s + (i.amount_due || 0), 0)
    const approved = rq.filter((q: any) => ['approved', 'invoiced'].includes(q.status)).length
    const convRate = rq.length > 0 ? Math.round((approved / rq.length) * 100) : 0
    return { collected, outstanding, overdue, convRate, approved, quoteTotal: rq.length }
  }, [ri, rq])

  // Gross margin (internal, computed-on-read). JOB-LEVEL EXCLUSION extended for
  // slice 2: a job counts in the aggregate only if it has line items AND every
  // line cost is known AND (it is NOT subbed OR its vendor bill has landed). So
  // a partially line-costed job OR a subbed job still awaiting its vendor bill is
  // excluded — neither inflates margin. Counted jobs fold vendor cost into their
  // cost (totalCost = line cost + vendor actual). Coverage (fully vs with-lines)
  // is surfaced so the % is read with its completeness in mind.
  const marginAgg = useMemo(() => {
    const rangeJobIds = new Set(rj.map((j: any) => j.id))
    const byJob = new Map<string, Array<{ unit_price: number; qty: number; unit_cost: number | null }>>()
    for (const l of jobLines as any[]) {
      if (!rangeJobIds.has(l.field_job_id)) continue
      if (!byJob.has(l.field_job_id)) byJob.set(l.field_job_id, [])
      byJob.get(l.field_job_id)!.push({ unit_price: Number(l.unit_price) || 0, qty: Number(l.qty) || 0, unit_cost: l.unit_cost ?? null })
    }
    // Vendor actual summed per job; set of jobs that are subbed (have assignments).
    const vendorByJob = new Map<string, number>()
    for (const b of jobBills as any[]) {
      if (!rangeJobIds.has(b.field_job_id)) continue
      vendorByJob.set(b.field_job_id, (vendorByJob.get(b.field_job_id) || 0) + (Number(b.amount) || 0))
    }
    const subbed = new Set<string>()
    for (const a of jobAssignments as any[]) {
      if (a.field_job_id && rangeJobIds.has(a.field_job_id)) subbed.add(a.field_job_id)
    }
    // Labor cost summed per job (slice 3). N/A-when-absent → never causes exclusion.
    const laborByJob = new Map<string, number>()
    for (const l of jobLabor as any[]) {
      if (!rangeJobIds.has(l.field_job_id)) continue
      laborByJob.set(l.field_job_id, (laborByJob.get(l.field_job_id) || 0) + (Number(l.amount) || 0))
    }

    let revenue = 0, cost = 0, fullyCosted = 0
    const jobsWithLines = byJob.size
    for (const [jobId, lines] of byJob.entries()) {
      const vendorActual = vendorByJob.has(jobId) ? vendorByJob.get(jobId)! : null
      const laborCost = laborByJob.has(jobId) ? laborByJob.get(jobId)! : null
      const m = jobMarginFull(lines, vendorActual, laborCost, { hasVendorAssignments: subbed.has(jobId) })
      if (m.isPartial) continue // line costs incomplete OR subbed-but-unbilled → exclude (labor never excludes)
      revenue += m.revenue; cost += m.totalCost; fullyCosted++
    }
    const margin = revenue - cost
    const marginPct = revenue === 0 ? null : margin / revenue
    return { revenue, cost, margin, marginPct, fullyCosted, jobsWithLines }
  }, [jobLines, jobBills, jobAssignments, jobLabor, rj])

  const conv = useMemo(() => {
    const resolved = rc.filter((c: any) => c.status === 'resolved').length
    const resRate = rc.length > 0 ? Math.round((resolved / rc.length) * 100) : 0
    const byChannel = ['email', 'chat', 'phone', 'sms', 'portal']
      .map(ch => ({ name: ch, value: rc.filter((c: any) => c.channel === ch).length }))
      .filter(c => c.value > 0)
    const byPriority = ['urgent', 'high', 'normal', 'low']
      .map(p => ({ name: p, value: rc.filter((c: any) => c.priority === p).length }))
      .filter(p => p.value > 0)
    return { total: rc.length, resolved, resRate, byChannel, byPriority }
  }, [rc])

  const dealM = useMemo(() => {
    const won = rd.filter((d: any) => d.status === 'won').length
    const lost = rd.filter((d: any) => d.status === 'lost').length
    const winRate = (won + lost) > 0 ? Math.round((won / (won + lost)) * 100) : 0
    const wonValue = rd.filter((d: any) => d.status === 'won').reduce((s: number, d: any) => s + (d.value || 0), 0)
    const totalValue = rd.reduce((s: number, d: any) => s + (d.value || 0), 0)
    const avgSize = rd.length > 0 ? totalValue / rd.length : 0
    const byStage = rd.reduce((acc: any, d: any) => {
      const s = d.stage_name || 'Unknown'; acc[s] = (acc[s] || 0) + 1; return acc
    }, {})
    return { total: rd.length, won, lost, winRate, wonValue, avgSize, byStage }
  }, [rd])

  const jobM = useMemo(() => {
    const complete = rj.filter((j: any) => j.status === 'complete').length
    const rate = rj.length > 0 ? Math.round((complete / rj.length) * 100) : 0
    const cancelled = rj.filter((j: any) => j.status === 'cancelled').length
    const held = rj.filter((j: any) => HOLD_STATUSES.includes(j.status)).length
    const collected = rj.filter((j: any) => j.payment_collected).reduce((s: number, j: any) => s + (j.payment_amount || 0), 0)
    const byAgent = Object.values(rj.reduce((acc: any, j: any) => {
      const n = j.assigned_agent_name || 'Unassigned'
      if (!acc[n]) acc[n] = { name: n, total: 0, complete: 0 }
      acc[n].total++; if (j.status === 'complete') acc[n].complete++
      return acc
    }, {}))
    return { total: rj.length, complete, rate, cancelled, held, collected, byAgent }
  }, [rj])

  const repM = useMemo(() => {
    const avg = rr.length > 0 ? rr.reduce((s: number, r: any) => s + (r.rating || 0), 0) / rr.length : 0
    const fiveStar = rr.filter((r: any) => r.rating === 5).length
    const responded = rr.filter((r: any) => r.status === 'responded').length
    const respRate = rr.length > 0 ? Math.round((responded / rr.length) * 100) : 0
    const npsR = rn.filter((n: any) => n.score !== null)
    const promoters = npsR.filter((n: any) => n.score >= 9).length
    const detractors = npsR.filter((n: any) => n.score <= 6).length
    const npsScore = npsR.length > 0 ? Math.round(((promoters - detractors) / npsR.length) * 100) : null
    const byPlatform = rr.reduce((acc: any, r: any) => { acc[r.platform] = (acc[r.platform] || 0) + 1; return acc }, {})
    return { avg: Math.round(avg * 10) / 10, fiveStar, respRate, npsScore, byPlatform }
  }, [rr, rn])

  // ── Time series ────────────────────────────────────────────────────────────
  const revSeries = useMemo(() => buildTimeSeries(
    invoices.filter((i: any) => i.status === 'paid'), 'paid_at', start, end, range, 'total'
  ), [invoices, start, end, range])

  const convSeries = useMemo(() => buildTimeSeries(conversations, 'created_at', start, end, range), [conversations, start, end, range])
  const jobSeries = useMemo(() => buildTimeSeries(fieldJobs, 'created_at', start, end, range), [fieldJobs, start, end, range])

  const SECTIONS = [
    { id: 'all', label: 'Overview' },
    { id: 'revenue', label: 'Revenue' },
    { id: 'conversations', label: 'Conversations' },
    { id: 'deals', label: 'Deals' },
    { id: 'field', label: 'Field' },
    { id: 'reputation', label: 'Reputation' },
  ]

  const show = (s: string) => activeSection === 'all' || activeSection === s

  // Zero-data short-circuit. Charts would render as flat lines at zero on every axis
  // and "0%" trend indicators that look like bugs to a fresh-signup operator. Replace
  // the entire dashboard with an invitation-to-action when no first-class entity has
  // any rows yet. Reviews + NPS are excluded from the check — those are common to be
  // empty even on a populated workspace. See Session 30 audit H5.
  const navigate = useNavigate()
  const hasAnyData = conversations.length > 0 || invoices.length > 0 || quotes.length > 0 || deals.length > 0 || fieldJobs.length > 0
  if (!hasAnyData && !anyError) {
    return (
      <div style={{ height: '100%', overflow: 'auto', background: 'var(--canvas)' }}>
        <div style={{ padding: '24px 28px', maxWidth: 1200 }}>
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Analytics</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>Insights across your conversations, deals, jobs, and revenue</p>
          </div>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            style={{ maxWidth: 560, margin: '40px auto 0', textAlign: 'center', padding: '40px 32px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 18 }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
              <BarChart style={{ width: 26, height: 26, color: 'var(--accent-text)' }} />
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>You don't have data yet</h2>
            <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 24px', lineHeight: 1.6 }}>
              Analytics charts will fill in as you create deals, schedule jobs, and send invoices. Start by adding a contact or creating your first deal.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/app/contacts')} className="btn-ghost" style={{ fontSize: 13 }}>Add your first contact</button>
              <button onClick={() => navigate('/app/deals')} className="btn-primary" style={{ fontSize: 13 }}>Create your first deal</button>
              <button onClick={() => navigate('/app/field')} className="btn-ghost" style={{ fontSize: 13 }}>Schedule a job</button>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px', maxWidth: 1200 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Analytics</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {format(start, 'MMM d')} — {format(end, 'MMM d, yyyy')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden' }}>
              {RANGE_OPTIONS.map(opt => (
                <button key={opt.id} onClick={() => setRange(opt.id)}
                  style={{ padding: '7px 14px', background: range === opt.id ? 'var(--accent)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: range === opt.id ? 'var(--accent-ink)' : 'var(--ink-secondary)', transition: 'all 150ms ease' }}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="date" value={customStart} onChange={e => { setCustomStart(e.target.value); setRange('custom') }}
                style={{ padding: '7px 10px', background: 'var(--field-bg)', border: `1px solid ${range === 'custom' ? 'var(--accent-border)' : 'var(--border-default)'}`, borderRadius: 8, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', colorScheme: 'dark' }} />
              <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>to</span>
              <input type="date" value={customEnd} onChange={e => { setCustomEnd(e.target.value); setRange('custom') }}
                style={{ padding: '7px 10px', background: 'var(--field-bg)', border: `1px solid ${range === 'custom' ? 'var(--accent-border)' : 'var(--border-default)'}`, borderRadius: 8, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', colorScheme: 'dark' }} />
            </div>
          </div>
        </div>

        {/* Section nav */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap' }}>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              style={{ padding: '7px 16px', borderRadius: 9, fontSize: 13, fontWeight: activeSection === s.id ? 600 : 400, cursor: 'pointer', background: activeSection === s.id ? 'var(--accent)' : 'var(--hover-bg)', color: activeSection === s.id ? 'var(--accent-ink)' : 'var(--ink-secondary)', border: activeSection === s.id ? 'none' : '1px solid var(--border-default)', transition: 'all 150ms ease' }}>
              {s.label}
            </button>
          ))}
        </div>

        {anyError && (
          <div style={{
            margin: '0 0 20px',
            padding: '12px 16px',
            background: 'var(--danger-dim)',
            border: '1px solid var(--danger-border)',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <AlertCircle style={{ width: 16, height: 16, color: 'var(--danger)', flexShrink: 0 }} />
            <p style={{ fontSize: 13, color: 'var(--ink-primary)', margin: 0, lineHeight: 1.5 }}>
              Some metrics could not be loaded. The numbers below may be incomplete. Try refreshing the page.
            </p>
          </div>
        )}

        {/* Overview */}
        {activeSection === 'all' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
            <StatCard label="Revenue collected" value={formatCurrency(revenue.collected)} color={COLORS.neon} icon={DollarSign} />
            <StatCard label="Conversations" value={rc.length} sub={`${conv.resRate}% resolved`} color={COLORS.primary} icon={MessageSquare} />
            <StatCard label="Jobs completed" value={jobM.complete} sub={`${jobM.rate}% rate`} color={COLORS.amber} icon={Truck} />
            <StatCard label="Avg review rating" value={repM.avg > 0 ? `${repM.avg}★` : '—'} sub={`${rr.length} reviews`} color={COLORS.amber} icon={Star} />
            <StatCard label="Deals won" value={dealM.won} sub={`${dealM.winRate}% win rate`} color={COLORS.blue} icon={Target} />
            <StatCard label="Quote conversion" value={`${revenue.convRate}%`} sub={`${revenue.approved}/${revenue.quoteTotal} approved`} color={COLORS.purple2} icon={TrendingUp} />
          </div>
        )}

        {/* Revenue */}
        {show('revenue') && (
          <>
            {/* Hero ticker — revenue recognized at job completion. Independent
                of the Analytics page's range picker (the ticker has its own
                Today/7D/30D/MTD/12M/YTD tabs). Sits above the invoice-based
                area chart, which shows a different lens (cash collected). */}
            <RevenueTicker jobs={fieldJobs} isMobile={isMobile} />
            {/* Projections — three-card period view + cumulative recognized-vs-
                projected mini-chart. Shares the revenueData primitives with
                the ticker so semantics never drift. */}
            <RevenueProjections jobs={fieldJobs} isMobile={isMobile} />
            {activeSection === 'revenue' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                <StatCard label="Collected" value={formatCurrency(revenue.collected)} color={COLORS.neon} icon={DollarSign} />
                <StatCard label="Outstanding" value={formatCurrency(revenue.outstanding)} color={COLORS.blue} icon={Clock} />
                <StatCard label="Overdue" value={formatCurrency(revenue.overdue)} color={COLORS.coral} icon={AlertTriangle} />
                <StatCard label="Quote conversion" value={`${revenue.convRate}%`} sub={`${revenue.approved}/${revenue.quoteTotal}`} color={COLORS.primary} icon={TrendingUp} />
                <StatCard
                  label="Gross margin (internal)"
                  value={marginAgg.fullyCosted === 0 ? '—' : formatMarginPct(marginAgg.marginPct)}
                  sub={marginAgg.fullyCosted === 0
                    ? 'no fully-costed jobs in range'
                    : `${formatCurrency(marginAgg.margin)} · ${marginAgg.fullyCosted}/${marginAgg.jobsWithLines} jobs fully costed`}
                  color={COLORS.purple2} icon={TrendingUp} />
              </div>
            )}
            <Section title="Revenue collected over time">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={revSeries}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--positive)" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="var(--positive)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hover-bg)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                  <Tooltip content={<CustomTooltip isCurrency />} />
                  <Area type="monotone" dataKey="value" stroke="var(--positive)" strokeWidth={2} fill="url(#revGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </Section>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Section title="Invoice status">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { label: 'Paid', value: ri.filter((i: any) => i.status === 'paid').length, color: 'var(--positive)' },
                    { label: 'Sent/Viewed', value: ri.filter((i: any) => ['sent', 'viewed'].includes(i.status)).length, color: 'var(--info)' },
                    { label: 'Draft', value: ri.filter((i: any) => i.status === 'draft').length, color: 'var(--ink-tertiary)' },
                    { label: 'Overdue', value: ri.filter((i: any) => i.status === 'overdue').length, color: 'var(--danger)' },
                  ].map(item => <BarRow key={item.label} {...item} total={ri.length} />)}
                </div>
              </Section>
              <Section title="Quote pipeline">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { label: 'Draft', value: rq.filter((q: any) => q.status === 'draft').length, color: 'var(--ink-tertiary)' },
                    { label: 'Sent', value: rq.filter((q: any) => q.status === 'sent').length, color: 'var(--info)' },
                    { label: 'Approved', value: rq.filter((q: any) => q.status === 'approved').length, color: 'var(--positive)' },
                    { label: 'Invoiced', value: rq.filter((q: any) => q.status === 'invoiced').length, color: 'var(--warning)' },
                    { label: 'Declined', value: rq.filter((q: any) => q.status === 'declined').length, color: 'var(--danger)' },
                  ].map(item => <BarRow key={item.label} {...item} total={rq.length} />)}
                </div>
              </Section>
            </div>
          </>
        )}

        {/* Conversations */}
        {show('conversations') && (
          <>
            {activeSection === 'conversations' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                <StatCard label="Total" value={conv.total} color={COLORS.primary} icon={MessageSquare} />
                <StatCard label="Resolved" value={conv.resolved} sub={`${conv.resRate}% rate`} color={COLORS.neon} icon={CheckCircle2} />
                <StatCard label="Open" value={rc.filter((c: any) => c.status === 'open').length} color={COLORS.amber} icon={Clock} />
                <StatCard label="Urgent" value={rc.filter((c: any) => c.priority === 'urgent').length} color={COLORS.coral} icon={AlertTriangle} />
              </div>
            )}
            <Section title="Conversation volume">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={convSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hover-bg)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Section>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Section title="By channel">
                {conv.byChannel.length === 0
                  ? <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center', padding: '20px 0' }}>No data yet</p>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {conv.byChannel.map((c: any, i: number) => (
                        <BarRow key={c.name} label={c.name} value={c.value} total={conv.total} color={Object.values(COLORS)[i % 6]} />
                      ))}
                    </div>
                }
              </Section>
              <Section title="By priority">
                {conv.byPriority.length === 0
                  ? <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center', padding: '20px 0' }}>No data yet</p>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {conv.byPriority.map((p: any) => {
                        const colors: Record<string, string> = { urgent: 'var(--danger)', high: 'var(--warning)', normal: 'var(--accent)', low: 'var(--ink-tertiary)' }
                        return <BarRow key={p.name} label={p.name} value={p.value} total={conv.total} color={colors[p.name] || 'var(--accent)'} />
                      })}
                    </div>
                }
              </Section>
            </div>
          </>
        )}

        {/* Deals */}
        {show('deals') && (
          <>
            {activeSection === 'deals' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                <StatCard label="Total deals" value={dealM.total} color={COLORS.blue} icon={TrendingUp} />
                <StatCard label="Won" value={dealM.won} sub={formatCurrency(dealM.wonValue)} color={COLORS.neon} icon={CheckCircle2} />
                <StatCard label="Win rate" value={`${dealM.winRate}%`} color={COLORS.primary} icon={Target} />
                <StatCard label="Avg deal size" value={formatCurrency(dealM.avgSize)} color={COLORS.amber} icon={DollarSign} />
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Section title="Win / Loss breakdown">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { label: 'Won', value: dealM.won, color: 'var(--positive)' },
                    { label: 'Lost', value: dealM.lost, color: 'var(--danger)' },
                    { label: 'Open', value: Math.max(0, dealM.total - dealM.won - dealM.lost), color: 'var(--ink-tertiary)' },
                  ].map(item => <BarRow key={item.label} {...item} total={dealM.total} />)}
                </div>
              </Section>
              <Section title="Deals by stage">
                {Object.keys(dealM.byStage).length === 0
                  ? <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center', padding: '20px 0' }}>No data yet</p>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {Object.entries(dealM.byStage).map(([stage, count]: any, i) => {
                        const stageColors = ['var(--ink-tertiary)', 'var(--accent)', 'var(--info)', 'var(--warning)', 'var(--positive)']
                        return <BarRow key={stage} label={stage} value={count} total={dealM.total} color={stageColors[i % 5]} />
                      })}
                    </div>
                }
              </Section>
            </div>
          </>
        )}

        {/* Field */}
        {show('field') && (
          <>
            {activeSection === 'field' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                <StatCard label="Total jobs" value={jobM.total} color={COLORS.amber} icon={Truck} />
                <StatCard label="Completed" value={jobM.complete} sub={`${jobM.rate}% rate`} color={COLORS.neon} icon={CheckCircle2} />
                <StatCard label="Cancelled" value={jobM.cancelled} color={COLORS.coral} icon={AlertTriangle} />
                <StatCard label="On hold" value={jobM.held} color={COLORS.blue} icon={Pause} />
                <StatCard label="Revenue collected" value={formatCurrency(jobM.collected)} color={COLORS.primary} icon={DollarSign} />
              </div>
            )}
            <Section title="Jobs over time">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={jobSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hover-bg)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--ink-tertiary)', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="var(--warning)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Section>
            {(jobM.byAgent as any[]).length > 0 && (
              <Section title="Technician performance">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(jobM.byAgent as any[]).sort((a, b) => b.complete - a.complete).map((agent: any) => {
                    const rate = agent.total > 0 ? Math.round((agent.complete / agent.total) * 100) : 0
                    const color = rate >= 80 ? 'var(--positive)' : rate >= 50 ? 'var(--warning)' : 'var(--danger)'
                    return (
                      <div key={agent.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--hover-bg)', borderRadius: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent-ink)', flexShrink: 0 }}>
                          {agent.name.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', flex: 1 }}>{agent.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 80, height: 5, background: 'var(--border-default)', borderRadius: 100, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${rate}%`, background: color, borderRadius: 100 }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-secondary)', width: 40 }}>{agent.complete}/{agent.total}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color, width: 36, textAlign: 'right' }}>{rate}%</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Section>
            )}
          </>
        )}

        {/* Reputation */}
        {show('reputation') && (
          <>
            {activeSection === 'reputation' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
                <StatCard label="Avg rating" value={repM.avg > 0 ? `${repM.avg}★` : '—'} sub={`${rr.length} reviews`} color={COLORS.amber} icon={Star} />
                <StatCard label="5-star reviews" value={repM.fiveStar} color={COLORS.neon} icon={CheckCircle2} />
                <StatCard label="Response rate" value={`${repM.respRate}%`} color={COLORS.primary} icon={MessageSquare} />
                <StatCard label="NPS score"
                  value={repM.npsScore !== null ? repM.npsScore : '—'}
                  sub={repM.npsScore !== null ? (repM.npsScore >= 50 ? 'Excellent' : repM.npsScore >= 0 ? 'Good' : 'Needs work') : 'No data'}
                  color={repM.npsScore !== null ? (repM.npsScore >= 50 ? COLORS.neon : repM.npsScore >= 0 ? COLORS.amber : COLORS.coral) : 'var(--ink-tertiary)'}
                  icon={TrendingUp} />
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Section title="Reviews by platform">
                {Object.keys(repM.byPlatform).length === 0
                  ? <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center', padding: '20px 0' }}>No reviews in this period</p>
                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {Object.entries(repM.byPlatform).map(([platform, count]: any) => {
                        const pc: Record<string, string> = { google: '#4285f4', facebook: '#1877f2', yelp: '#ff1a1a', trustpilot: '#00b67a' } /* allow-hex: third-party brand colors — not theme-able */
                        return <BarRow key={platform} label={platform} value={count} total={rr.length} color={pc[platform] || COLORS.primary} />
                      })}
                    </div>
                }
              </Section>
              <Section title="Rating distribution">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[5, 4, 3, 2, 1].map(rating => {
                    const count = rr.filter((r: any) => Math.round(r.rating) === rating).length
                    const color = rating >= 4 ? 'var(--positive)' : rating === 3 ? 'var(--warning)' : 'var(--danger)'
                    return <BarRow key={rating} label={'★'.repeat(rating)} value={count} total={rr.length} color={color} />
                  })}
                </div>
              </Section>
            </div>
          </>
        )}

        {/* Empty state */}
        {activeSection === 'all' && rc.length === 0 && ri.length === 0 && rj.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', marginTop: 20 }}>
            <TrendingUp style={{ width: 40, height: 40, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>No data for this period</p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Try a wider date range or start adding data</p>
          </div>
        )}

      </div>
    </div>
  )
}